import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { IJob } from 'core/interface/job';
import { JobService } from 'services/jobs/job';
import { SaveSingleJobs } from '../redux/slices/jobs';
import useLocales from 'hooks/useLocales';

const useJobInlineSave = (job: IJob | null) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useLocales();
  const [saving, setSaving] = useState(false);

  const savePatch = useCallback(
    async (
      patch: Record<string, unknown>,
      optimistic?: Partial<IJob>,
      options?: { refetch?: boolean }
    ): Promise<boolean> => {
      if (!job?.id) {
        return false;
      }

      const previous = job;
      if (optimistic) {
        dispatch(SaveSingleJobs({ ...job, ...optimistic }));
      }
      setSaving(true);

      try {
        const res = await JobService.saveJobEdit(job.id, patch);
        if (!res) {
          dispatch(SaveSingleJobs(previous));
          enqueueSnackbar(t('Failed to update job'), { variant: 'error' });
          return false;
        }

        if (options?.refetch) {
          await JobService.getJobByIdOrSlug(job.id);
        }

        enqueueSnackbar(t('Job updated'), { variant: 'success' });
        return true;
      } catch (error) {
        dispatch(SaveSingleJobs(previous));
        enqueueSnackbar(t('Failed to update job'), { variant: 'error' });
        return false;
      } finally {
        setSaving(false);
      }
    },
    [dispatch, enqueueSnackbar, job, t]
  );

  return { savePatch, saving };
};

export default useJobInlineSave;
